import type { TagAllowlistTracker } from './tag-allowlist-tracker.ts'
import { defaultAllowedAttributes, defaultAllowedTags } from '@shared/markdown/defaults'
import sanitizeHtml from 'sanitize-html'

export interface SanitizeOptions {
  /** Tags + attrs allowed on top of defaults + the runtime allowlist. */
  extraTags?: Record<string, string[]>
}

/**
 * Sanitize function for the current proxy context. Reads the tracker
 * on every call, so tags registered via any load path become allowed
 * automatically. `getRegisteredTags` is injected so iframe / host
 * contexts can each supply their own source of truth.
 */
export function createSanitizeAPI(
  getRegisteredTags: () => Record<string, string[]>,
): (html: string, opts?: SanitizeOptions) => string {
  return function sanitize(html: string, opts?: SanitizeOptions): string {
    const registered = getRegisteredTags()
    const extra = opts?.extraTags ?? {}
    return sanitizeHtml(html, {
      allowedTags: [
        ...defaultAllowedTags,
        ...Object.keys(registered),
        ...Object.keys(extra),
      ],
      allowedAttributes: {
        ...defaultAllowedAttributes,
        ...registered,
        ...extra,
      },
    })
  }
}

/**
 * Extract attribute names from a JSON-schema-shaped `props.properties`
 * map. Returns `[]` for null / non-object / array shapes — the latter
 * matters because `typeof [] === 'object'` would otherwise yield
 * numeric index keys.
 */
export function extractPropAttrs(props: unknown): string[] {
  if (!props || typeof props !== 'object' || Array.isArray(props))
    return []
  const properties = (props as { properties?: unknown }).properties
  if (!properties || typeof properties !== 'object' || Array.isArray(properties))
    return []
  return Object.keys(properties)
}

export type { TagAllowlistTracker }
